import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
}

const DEFAULT_DOMAIN = 'https://www.minipt.co.uk';
const DEFAULT_OG_IMAGE = 'https://www.minipt.co.uk/og-image.jpg';

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonicalUrl,
  ogImage,
  ogType = 'website',
}) => {
  const domain = (process.env.DOMAIN || DEFAULT_DOMAIN).replace(/\/$/, '');
  const fullCanonicalUrl = canonicalUrl
    ? `${domain}${canonicalUrl.startsWith('/') ? canonicalUrl : `/${canonicalUrl}`}`
    : domain;
  const ogImageUrl = ogImage
    ? ogImage.startsWith('http')
      ? ogImage
      : `${domain}${ogImage.startsWith('/') ? ogImage : `/${ogImage}`}`
    : DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      {/* Basic HTML Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />

      {/* Canonical Link */}
      <link rel="canonical" href={fullCanonicalUrl} />

      {/* Open Graph / Facebook / LinkedIn / WhatsApp / Slack */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="miniPT" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:secure_url" content={ogImageUrl} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@miniPT" />
      <meta name="twitter:creator" content="@miniPT" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:image:alt" content={title} />
      <meta name="twitter:url" content={fullCanonicalUrl} />
    </Helmet>
  );
};

export default SEO;
